import { useEffect, useRef, useState } from "react";
import { keepPreviousData, useInfiniteQuery, useQuery, type InfiniteData, type QueryKey } from "@tanstack/react-query";
import { Link, useSearchParams } from "wouter";
import { gqlRequest } from "../lib/gql";
import { FEED, MY_FOLLOWEES, type FeedResult, type MyFolloweesResult } from "../lib/queries";
import { useAuth } from "../lib/auth";
import { ACTIVITIES, ACTIVITY_ICONS, type Activity } from "../lib/format";
import { useT } from "../lib/i18n";
import { OutingCard } from "../components/OutingCard";
import { Chip, EmptyState, ErrorBox, Select, Spinner, TextInput } from "../components/ui";

const PAGE_SIZE = 15;

type FeedTab = "everyone" | "following";

type FeedSort = "new" | "old" | "top";

// "top" relies on pdbq ≥ 0.9.1, where DESC order is NULLS LAST — unrated
// outings sort after rated ones. The DATE_DESC tiebreak also orders the
// all-null tail.
const FEED_ORDER: Record<FeedSort, string[]> = {
  new: ["DATE_DESC"],
  old: ["DATE_ASC"],
  top: ["RATING_DESC", "DATE_DESC"],
};

export function FeedPage() {
  const { personId } = useAuth();
  const { t, labelizeT } = useT();
  const [params, setParams] = useSearchParams();
  const [activities, setActivities] = useState<Activity[]>([]);
  const [dateFrom, setDateFrom] = useState("");
  const [minRating, setMinRating] = useState<number | null>(null);

  const tab: FeedTab = params.get("tab") === "following" ? "following" : "everyone";
  const setTab = (next: FeedTab) => {
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next === "following") p.set("tab", "following");
        else p.delete("tab");
        return p;
      },
      { replace: true },
    );
  };

  const sortParam = params.get("sort");
  const sort: FeedSort = sortParam === "old" || sortParam === "top" ? sortParam : "new";
  const setSort = (next: FeedSort) => {
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next === "new") p.delete("sort");
        else p.set("sort", next);
        return p;
      },
      { replace: true },
    );
  };

  const followingActive = tab === "following" && personId != null;

  const followees = useQuery({
    queryKey: ["my-followees", personId],
    enabled: followingActive,
    queryFn: () => gqlRequest<MyFolloweesResult>(MY_FOLLOWEES, { viewerId: personId }),
  });
  const followeeIds = followees.data?.personFollows.nodes.map((n) => n.followeeId);

  const filter: Record<string, unknown> = {};
  if (activities.length) filter.activity = { in: activities };
  if (dateFrom) filter.date = { greaterThanOrEqualTo: dateFrom };
  if (minRating != null) filter.rating = { greaterThanOrEqualTo: minRating };
  if (followingActive && followeeIds) {
    // Owned by a followee — participation alone doesn't surface an outing.
    filter.outingPeopleByOutingId = {
      some: { role: { equalTo: "OWNER" }, personId: { in: followeeIds } },
    };
  }

  const followingEmpty = followingActive && followeeIds != null && followeeIds.length === 0;
  // Hide stale "Everyone" cards (kept alive by keepPreviousData) while the
  // followee list is still loading after a tab switch.
  const showCards = !followingEmpty && (!followingActive || followeeIds != null);

  // Offset paging underneath an infinite query: pageParam IS the offset, and
  // any tab/filter/sort change swaps the query key, which resets pagination
  // to page one on its own.
  const { data, isLoading, isFetching, error, hasNextPage, isFetchingNextPage, fetchNextPage, isPlaceholderData } = useInfiniteQuery<
    FeedResult,
    Error,
    InfiniteData<FeedResult>,
    QueryKey,
    number
  >({
    queryKey: ["feed", tab, followingActive ? followeeIds : null, activities, dateFrom, minRating, sort],
    placeholderData: keepPreviousData,
    // Wait for the followee list before fetching a Following page, and skip
    // the fetch entirely when the viewer follows nobody.
    enabled: !followingActive || (followeeIds != null && followeeIds.length > 0),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, p) => n + p.outings.nodes.length, 0);
      return loaded < lastPage.outings.totalCount ? loaded : undefined;
    },
    queryFn: ({ pageParam }) =>
      gqlRequest<FeedResult>(FEED, {
        first: PAGE_SIZE,
        offset: pageParam,
        filter: Object.keys(filter).length ? filter : null,
        orderBy: FEED_ORDER[sort],
      }),
  });

  const outingsList = showCards ? (data?.pages.flatMap((p) => p.outings.nodes) ?? []) : [];
  const total = followingEmpty ? 0 : (data?.pages[0]?.outings.totalCount ?? 0);
  const waiting = isLoading || (followingActive && followees.isLoading);

  // Fetch the next page when the sentinel under the list scrolls near the
  // viewport. Re-observing when isFetchingNextPage flips back to false covers
  // the case where the sentinel is still visible after a page lands. Never
  // observe over placeholder data: its offsets belong to the previous query
  // key, so a "next page" fetched from them would be wrong.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasNextPage || isFetchingNextPage || isPlaceholderData) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) fetchNextPage();
      },
      { rootMargin: "400px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, isPlaceholderData, fetchNextPage, outingsList.length]);

  const tabClass = (active: boolean) =>
    `rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors ${
      active ? "bg-card text-foreground shadow-card" : "text-muted-foreground hover:text-foreground"
    }`;

  return (
    <div className="mx-auto max-w-2xl p-4">
      <h1 className="mb-4 text-2xl font-semibold tracking-tight text-foreground">{t("feed.title")}</h1>

      <div className="mb-4 inline-flex gap-1 rounded-xl bg-muted p-1" role="tablist" aria-label={t("nav.feed")}>
        <button type="button" role="tab" aria-selected={tab === "everyone"} className={tabClass(tab === "everyone")} onClick={() => setTab("everyone")}>
          {t("feed.everyone")}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "following"}
          className={tabClass(tab === "following")}
          onClick={() => setTab("following")}
        >
          {t("feed.following")}
        </button>
      </div>

      <div className="mb-3 flex gap-2 overflow-x-auto pb-1">
        {ACTIVITIES.map((a) => {
          const Icon = ACTIVITY_ICONS[a];
          return (
            <Chip
              key={a}
              active={activities.includes(a)}
              onClick={() => setActivities((prev) => (prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]))}
            >
              <span className="inline-flex items-center gap-1.5">
                <Icon aria-hidden className="size-3.5" />
                {labelizeT(a)}
              </span>
            </Chip>
          );
        })}
      </div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          {t("feed.since")}
          <TextInput
            type="date"
            className="!w-auto"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </label>
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          {t("feed.minRating")}
          <Select
            className="!w-auto"
            value={minRating ?? ""}
            onChange={(e) => setMinRating(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">{t("common.any")}</option>
            {[3, 4, 5].map((n) => (
              <option key={n} value={n}>
                {n}★+
              </option>
            ))}
          </Select>
        </label>
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          {t("common.sort")}
          <Select className="!w-auto" value={sort} onChange={(e) => setSort(e.target.value as FeedSort)}>
            <option value="new">{t("feed.sortNew")}</option>
            <option value="old">{t("feed.sortOld")}</option>
            <option value="top">{t("feed.sortTop")}</option>
          </Select>
        </label>
        {isFetching && !isLoading && !isFetchingNextPage && (
          <span className="text-xs text-muted-foreground">{t("feed.updating")}</span>
        )}
      </div>

      {tab === "following" && personId == null ? (
        <EmptyState>
          <Link href={`/login?next=${encodeURIComponent("/feed?tab=following")}`} className="font-semibold text-primary hover:underline">
            {t("common.logIn")}
          </Link>{" "}
          {t("feed.loginToSeeFollowing")}
        </EmptyState>
      ) : (
        <>
          {waiting && <Spinner />}
          {error != null && <ErrorBox error={error} />}
          {followees.error != null && <ErrorBox error={followees.error} />}
          {followingEmpty && <EmptyState>{t("feed.notFollowingAnyone")}</EmptyState>}
          {showCards && data && outingsList.length === 0 && (
            <EmptyState>{followingActive ? t("feed.emptyFollowing") : t("feed.emptyAll")}</EmptyState>
          )}

          <div className="space-y-3">
            {outingsList.map((outing) => (
              <OutingCard key={outing.id} outing={outing} />
            ))}
          </div>

          {isFetchingNextPage && (
            <div className="mt-4 flex items-center justify-center gap-2 py-2 text-sm text-muted-foreground">
              <span className="size-4 animate-spin rounded-full border-2 border-muted border-t-primary" />
              {t("feed.loadingMore")}
            </div>
          )}
          {showCards && outingsList.length > 0 && !hasNextPage && (
            <p className="mt-6 text-center text-sm text-muted-foreground">
              {t(total === 1 ? "feed.endOne" : "feed.endOther", { count: total })}
            </p>
          )}
          {showCards && hasNextPage && <div ref={sentinelRef} aria-hidden className="h-px" />}
        </>
      )}
    </div>
  );
}
